import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, Button } from "@mui/material";
import { SearchOff, HeadsetMicOutlined } from "@mui/icons-material";
import { colors } from "../../theme/colors";

export default function SearchEmptyState({
    title,
    description,
    searchQuery = "",
    onClearSearch,
    supportTicketHref,
}) {
    const trimmedQuery = String(searchQuery || "").trim();
    const showQueryChip = trimmedQuery.length > 0;
    const showClearSearch = showQueryChip && typeof onClearSearch === "function";
    const showSupportTicket = Boolean(supportTicketHref);
    const showActions = showClearSearch || showSupportTicket;

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "4rem",
                    height: "4rem",
                    border: `solid 1px ${colors.border.default}`,
                    borderRadius: "50%",
                    backgroundColor: colors.accent.subtle,
                }}
            >
                <SearchOff
                    sx={{ fontSize: "2rem", color: colors.accent.main }}
                />
            </Box>
            <Typography
                sx={{
                    textTransform: "uppercase",
                    fontSize: "1.25rem",
                    fontWeight: 600,
                    color: colors.text.primary,
                    mt: "1rem",
                    textAlign: "center",
                }}
            >
                {title}
            </Typography>
            {showQueryChip ? (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: "0.5rem",
                        mt: "0.5rem",
                        flexWrap: "wrap",
                        justifyContent: "center",
                    }}
                >
                    <Typography
                        sx={{
                            color: colors.text.secondary,
                            fontSize: "0.85rem",
                            fontWeight: 400,
                        }}
                    >
                        No results for
                    </Typography>
                    <Typography
                        component="span"
                        sx={{
                            padding: "0.25rem 0.45rem",
                            backgroundColor: colors.accent.subtle,
                            color: colors.accent.main,
                            border: `solid 1px ${colors.border.default}`,
                            borderRadius: "6px",
                            fontSize: "0.85rem",
                            fontWeight: 500,
                            maxWidth: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {trimmedQuery}.
                    </Typography>
                </Box>
            ) : null}
            <Typography
                sx={{
                    color: colors.text.secondary,
                    fontSize: "0.85rem",
                    fontWeight: 400,
                    mt: "0.5rem",
                    textAlign: "center",
                }}
            >
                {description}
            </Typography>

            {showActions ? (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: "0.75rem",
                        mt: "1.5rem",
                        flexWrap: "wrap",
                        justifyContent: "center",
                    }}
                >
                    {showClearSearch ? (
                        <Button
                            onClick={onClearSearch}
                            sx={{
                                color: colors.text.primary,
                                fontSize: "0.85rem",
                                fontWeight: 600,
                                textTransform: "uppercase",
                                borderRadius: "10px",
                                padding: "0.5rem 1.2rem",
                                backgroundColor: colors.primary.main,
                                letterSpacing: "0.08em",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                    backgroundColor: colors.primary.hover,
                                },
                            }}
                        >
                            Clear search
                        </Button>
                    ) : null}
                    {showSupportTicket ? (
                        <Button
                            component="a"
                            href={supportTicketHref}
                            sx={{
                                color: colors.text.secondary,
                                fontSize: "0.85rem",
                                fontWeight: 600,
                                textTransform: "uppercase",
                                borderRadius: "10px",
                                padding: "0.5rem 1.2rem",
                                backgroundColor: "transparent",
                                border: `solid 1px ${colors.border.default}`,
                                letterSpacing: "0.08em",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                    borderColor: colors.accent.main,
                                    color: colors.accent.main,
                                },
                            }}
                            startIcon={
                                <HeadsetMicOutlined
                                    sx={{ fontSize: "1.1rem !important" }}
                                />
                            }
                        >
                            Open a ticket
                        </Button>
                    ) : null}
                </Box>
            ) : null}
        </Box>
    );
}

SearchEmptyState.propTypes = {
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    searchQuery: PropTypes.string,
    onClearSearch: PropTypes.func,
    supportTicketHref: PropTypes.string,
};
